import { Grid } from '@mui/material';
import InfiniteScroll from 'components/InfiniteScroll/InfiniteScroll';
import BlockImage from 'components/layout/BlockImages/BlockImage/BlockImage';
import BlockImages from 'components/layout/BlockImages/BlockImages';
import Loader from 'components/Loader/Loader';
import { PRODUCT } from 'config/fe-routes';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProductService from 'services/product.service';
import { ProductType } from 'types/product.types';
import { GTM, GTMElements, GTMLocations } from 'utils/analytics';

const productService = new ProductService();

type RelatedProductsProps = {
  product: ProductType;
};

const RelatedProducts = (props: RelatedProductsProps) => {
  const { product } = props;
  const { t } = useTranslation();

  const [isRelatedProductsLoading, setRelatedProductsLoading] = useState(false);
  const [hasMoreRecommendations, setHasMoreRecommendations] = useState(true);
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    if (product.slug) {
      getRelatedProducts(product.slug);
    }
  }, [product]);

  const getRelatedProducts = async (productSlug: string) => {
    try {
      setRelatedProductsLoading(true);

      const list = await productService.getRecommendations(productSlug, undefined, { silentFail: true });

      setRecommendations(Array.isArray(list) ? list : []);
    } catch (e) {
      setHasMoreRecommendations(false);
    }
    setRelatedProductsLoading(false);
  };

  const loadMoreItems = async () => {
    try {
      const list = await productService.getRecommendations(product.slug, recommendations[recommendations.length - 1]?.listedAt, {
        silentFail: true,
      });

      if (!list || !list.length) {
        setHasMoreRecommendations(false);

        return;
      }

      const combinedRecommendations = recommendations.concat(list);

      setHasMoreRecommendations(() => list.length >= 60);
      setRecommendations(() => combinedRecommendations);
    } catch (e) {
      setHasMoreRecommendations(false);
    }
  };

  return (
    <Grid container className="related-products page-wide mt-8">
      {isRelatedProductsLoading || recommendations?.length ? <h2>{t('components.recommended-products.title')}</h2> : null}
      {isRelatedProductsLoading ? (
        <Loader />
      ) : (
        <BlockImages>
          <InfiniteScroll
            batchSize={60}
            dataLength={hasMoreRecommendations ? recommendations.length + 1 : recommendations.length}
            loadMore={() => loadMoreItems()}
            hasMore={hasMoreRecommendations}
            loader={<Loader />}
          >
            {recommendations?.map((recommendedProduct, index) => {
              const productImage = recommendedProduct?.images?.[0];

              return (
                <BlockImage
                  key={'product-' + recommendedProduct.slug}
                  productImage={productImage}
                  isCharity={recommendedProduct?.tags?.includes('Charity') || recommendedProduct?.isCharityProduct}
                  alt={productImage?.fileName}
                  price={recommendedProduct.price}
                  priceDrop={recommendedProduct.priceDrop}
                  productSlug={recommendedProduct.slug}
                  status={recommendedProduct.status}
                  details={recommendedProduct?.attributes?.size}
                  brand={recommendedProduct.brand}
                  tagged={recommendedProduct.tagged}
                  pinned={!!recommendedProduct.pinnedAt}
                  globalLoaderEnabled={true}
                  productId={recommendedProduct.id}
                  shopId={recommendedProduct.shopId}
                  likeCount={recommendedProduct.likeCount}
                  isOwner={recommendedProduct.isOwner}
                  isNewLister={recommendedProduct?.shop?.isNewLister}
                  {...PRODUCT(recommendedProduct?.shop?.activeSlug, recommendedProduct.slug, undefined, index)}
                  onClick={() => {
                    // Tracking recommendation click
                    GTM.elementClicked(GTMElements.RECOMMENDED_ITEM, GTMLocations.LISTING_PAGE, {
                      original_product_slug: product.slug,
                      original_product_id: product.id,
                      clicked_product_slug: recommendedProduct.slug,
                      clicked_product_id: recommendedProduct.id,
                      clicked_product_shop_slug: recommendedProduct.shop?.activeSlug,
                    });
                  }}
                />
              );
            })}
          </InfiniteScroll>
        </BlockImages>
      )}
    </Grid>
  );
};

export default RelatedProducts;
